import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLang } from '../i18n/LanguageContext';
import { SITE_ORIGIN } from './Seo';
import styles from './NotFound.module.css';
import FilmGrain, { GRAIN_INTENSITY } from './FilmGrain';

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatLiveTimecode(date) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const hour = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const minute = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const second = parts.find((p) => p.type === 'second')?.value ?? '00';
  const frames = Math.floor((date.getMilliseconds() / 1000) * 25);

  return `${hour}:${minute}:${second}:${pad2(frames)}`;
}

export default function NotFound() {
  const { lang, t, tr, buildPath } = useLang();
  const reducedMotion = typeof window !== 'undefined'
    && window.matchMedia
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [timecode, setTimecode] = useState('23:31:46:13');

  useEffect(() => {
    if (reducedMotion) return undefined;
    const tick = () => setTimecode(formatLiveTimecode(new Date()));
    tick();
    const id = window.setInterval(tick, 40);
    return () => window.clearInterval(id);
  }, [reducedMotion]);

  const enTitle = 'Page not found | EDELNAD Productions';
  const deTitle = 'Seite nicht gefunden | EDELNAD Productions';
  const title = lang === 'de' ? deTitle : enTitle;
  const canonical = `${SITE_ORIGIN}${lang === 'de' ? '/de' : ''}/404`;

  return (
    <section className={styles.section} aria-label={t('notfound.heading')}>
      <Helmet>
        <html lang={lang} />
        <title>{title}</title>
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={`${SITE_ORIGIN}/404`} />
        <link rel="alternate" hrefLang="de" href={`${SITE_ORIGIN}/de/404`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITE_ORIGIN}/404`} />
      </Helmet>

      {/* Camera HUD overlays */}
      <div className={styles.hud} aria-hidden="true">
        <div className={styles.hudTopLeft}>
          <span className={styles.timecode}>{timecode}</span>
        </div>
        <div className={styles.hudTopRight}>
          <span className={styles.recDot} />
          <span className={styles.recLabel}>REC</span>
        </div>

        <span className={styles.corner} data-pos="tl" />
        <span className={styles.corner} data-pos="tr" />
        <span className={styles.corner} data-pos="bl" />
        <span className={styles.corner} data-pos="br" />
      </div>

      <div className={styles.content}>
        <h1 className={styles.fourOhFour}>404</h1>
        <p className={styles.heading}>{t('notfound.heading')}</p>
        <p className={styles.subtext}>
          {tr(
            <>
              Looks like this page took a wrong turn on the way to the next amazing story.
              Let&apos;s get you back on track.
            </>,
            <>
              Diese Seite hat sich auf dem Weg zur nächsten großartigen Geschichte verlaufen.
              Zurück auf Kurs geht es hier.
            </>,
          )}
        </p>
        <Link to={buildPath('/')} className={`btn-gradient ${styles.cta}`}>
          {t('notfound.cta')}
        </Link>
      </div>

      <FilmGrain intensity={GRAIN_INTENSITY.whisper} />
    </section>
  );
}
